import { AggregationType, AxisSettings, GlobalSettings } from '@twaice-fe/shared/models';
import { Sensor } from './data-explorer-types';
import { getScaledUnit } from '../unit-scaling.util';

export function buildDeAxisSettings(params: {
  selectedSensors: Sensor[];
  currentAxis: AxisSettings[];
  globalSettings: GlobalSettings;
  formatSensorType: (sensorType: string) => string;
}): AxisSettings[] {
  const { selectedSensors, currentAxis, globalSettings, formatSensorType } = params;
  const newAxis: AxisSettings[] = [];

  const sensorTypes = selectedSensors.map((sensor) => sensor.key);

  const unitScalingCombinations = new Map<string, { unit: string; name: string; scalingFactor: number }>();

  selectedSensors.forEach((sensor) => {
    let key: string;
    let unitItem: { unit: string; name: string; scalingFactor: number };

    if (sensor.valueType === 'position') {
      key = 'position';
      unitItem = { unit: 'position', name: 'Position', scalingFactor: 1 };
    } else if (sensor.unit === '') {
      key = sensor.sensorType;
      unitItem = {
        unit: sensor.sensorType,
        name: formatSensorType(sensor.sensorType),
        scalingFactor: 1,
      };
    } else {
      // Create unique key based on unit and scaling factor
      key = `${sensor.unit}_${sensor.scalingFactor || 1}`;
      unitItem = { unit: sensor.unit, name: sensor.sensorType, scalingFactor: sensor.scalingFactor || 1 };
    }

    unitScalingCombinations.set(key, unitItem);
  });

  const sensorUnits = Array.from(unitScalingCombinations.values());

  // Take still valid entries from old list to new list
  if (currentAxis) {
    currentAxis.forEach((axisItem) => {
      if (
        (globalSettings.groupBy === 'sensor' && axisItem.key && sensorTypes.includes(axisItem.key)) ||
        (globalSettings.groupBy === 'unit' &&
          axisItem.key === undefined &&
          sensorUnits.some((unitItem: { unit: string; scalingFactor: number }) => {
            const scaledUnit = getScaledUnit(unitItem.unit, unitItem.scalingFactor);
            return unitItem.unit === axisItem.unit && axisItem.name === `[${scaledUnit}]`;
          }))
      ) {
        // Update existing axis item with correct scaling.
        if (globalSettings.groupBy === 'unit' && axisItem.unit) {
          const sensorsWithUnit = selectedSensors.filter(
            (s) => s.unit === axisItem.unit && s.scalingFactor === axisItem.scalingFactor
          );
          const scalingFactor = sensorsWithUnit.length > 0 ? sensorsWithUnit[0].scalingFactor : 1;
          const scaledUnit = getScaledUnit(axisItem.unit, scalingFactor);
          axisItem.name = `[${scaledUnit}]`;
          axisItem.scalingFactor = scalingFactor;
        }
        newAxis.push({ ...axisItem });
      }
    });
  }

  // Add missing entries
  if (globalSettings.groupBy === 'sensor') {
    if (selectedSensors) {
      selectedSensors.forEach((sensor) => {
        if (!newAxis.some((existingItem) => existingItem.key === sensor.key)) {
          newAxis.push({
            key: sensor.key,
            name: sensor.name,
            unit: sensor.unit,
            scalingFactor: sensor.scalingFactor,
            chartType: 'line',
            lineStyle: 'linear',
            displayDataPoints: false,
            aggregationType: getDefaultAggregationType(sensor),
            zeroYAxis: false,
            open: false,
          });
        }
      });
    }
  } else {
    if (sensorUnits) {
      sensorUnits.forEach((unitItem) => {
        if (
          !newAxis.some(
            (existingItem) =>
              (existingItem.unit === unitItem.unit && existingItem.scalingFactor === unitItem.scalingFactor) ||
              existingItem.name === unitItem.name
          )
        ) {
          const isPositionUnit = unitItem.unit === 'position' && unitItem.name === 'Position';
          const defaultAggregationType = getDefaultAggregationTypeForUnit(selectedSensors, unitItem, isPositionUnit);

          if (isPositionUnit) {
            newAxis.push({
              key: undefined,
              name: `Position`,
              unit: '',
              scalingFactor: 1,
              chartType: 'line',
              lineStyle: 'linear',
              displayDataPoints: false,
              aggregationType: defaultAggregationType,
              zeroYAxis: false,
              open: false,
            });
          } else if (formatSensorType(unitItem.unit) === unitItem.name) {
            newAxis.push({
              key: undefined,
              name: unitItem.name,
              unit: '',
              scalingFactor: 1,
              chartType: 'line',
              lineStyle: 'linear',
              displayDataPoints: false,
              aggregationType: defaultAggregationType,
              zeroYAxis: false,
              open: false,
            });
          } else {
            const sensorsWithUnit = selectedSensors.filter(
              (s) => s.unit === unitItem.unit && s.scalingFactor === unitItem.scalingFactor
            );
            const scalingFactor = sensorsWithUnit.length > 0 ? sensorsWithUnit[0].scalingFactor : 1;
            const scaledUnit = getScaledUnit(unitItem.unit, scalingFactor);

            newAxis.push({
              key: undefined,
              name: `[${scaledUnit}]`,
              unit: unitItem.unit,
              scalingFactor: scalingFactor,
              chartType: 'line',
              lineStyle: 'linear',
              displayDataPoints: false,
              aggregationType: defaultAggregationType,
              zeroYAxis: false,
              open: false,
            });
          }
        }
      });
    }
  }

  return newAxis;
}

function getDefaultAggregationType(sensor: Sensor): AggregationType {
  if (sensor.valueType === 'position') {
    return AggregationType.LAST;
  }
  if (sensor.aggregationType === 'max') {
    return AggregationType.MAX;
  }
  if (sensor.aggregationType === 'min') {
    return AggregationType.MIN;
  }
  return AggregationType.AVG;
}

function getDefaultAggregationTypeForUnit(
  selectedSensors: Sensor[],
  unitItem: {
    unit: string;
    name: string;
    scalingFactor?: number;
  },
  isPositionUnit: boolean
): AggregationType {
  if (isPositionUnit) {
    return AggregationType.LAST;
  }

  const sensorsInUnit = selectedSensors.filter((sensor) => {
    if (sensor.valueType === 'position') return unitItem.unit === 'position';
    if (sensor.unit === '') return unitItem.unit === sensor.sensorType;
    return sensor.unit === unitItem.unit && sensor.scalingFactor === unitItem.scalingFactor;
  });

  const groupAggregationTypes = [...new Set(sensorsInUnit.map((s) => s.aggregationType))];
  if (groupAggregationTypes.length === 1) {
    if (groupAggregationTypes[0] === 'max') return AggregationType.MAX;
    if (groupAggregationTypes[0] === 'min') return AggregationType.MIN;
  }
  return AggregationType.AVG;
}
